import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, MapPin, ShoppingBag, Truck } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { useCart } from "../../context/CartContext";
import { shopApi, fmt, formatApiError } from "../../lib/shopApi";
import { PasswordInput } from "../../components/shop/PasswordInput";

const PROVINCES = ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape"];

const Input = ({ label, ...props }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">{label}</label>
    <input
      {...props}
      className="w-full bg-[#232323] border border-[#C9A24B]/20 rounded-xl px-4 py-3.5 text-[#F5EFE3] placeholder-[#F5EFE3]/30 focus:outline-none focus:border-[#C9A24B] transition-colors"
    />
  </div>
);

const CheckoutPage = () => {
  const { items, subtotalCents, clearCart } = useCart();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", street: "", city: "", province: "Gauteng", postal: "" });
  const [delivery, setDelivery] = useState("paxi");
  const [payment] = useState("ikhokha");
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const hasMadeToOrder = items.some((i) => i.made_to_order);

  useEffect(() => {
    shopApi.get("/shop/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const deliveryFee = config?.delivery_options?.find((o) => o.key === delivery)?.fee_cents ?? 0;
  const total = subtotalCents - (discount?.discount_cents || 0) + deliveryFee;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const applyDiscount = async () => {
    setDiscountError("");
    setDiscount(null);
    if (!discountCode.trim()) return;
    try {
      const r = await shopApi.post("/shop/discounts/validate", { code: discountCode, subtotal_cents: subtotalCents });
      setDiscount(r.data);
    } catch (e) {
      setDiscountError(formatApiError(e, "Invalid discount code"));
    }
  };

  const placeOrder = async () => {
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      setError("Please fill in your name, phone and email.");
      return;
    }
    if (delivery !== "collection" && (!form.street.trim() || !form.city.trim() || !form.postal.trim())) {
      setError("Please fill in your full delivery address.");
      return;
    }
    if (createAccount && accountPassword.length < 6) {
      setError("Please choose a password of at least 6 characters for your account.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        customer: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          address: delivery === "paxi" ? { street: form.street, city: form.city, province: form.province, postal: form.postal } : null,
        },
        items: items.map((i) => ({ product_id: i.product_id, size_label: i.size_label, qty: i.qty, options: i.options || {} })),
        delivery_method: delivery,
        payment_method: payment,
        discount_code: discount?.code || null,
      };
      const r = await shopApi.post("/shop/orders", payload);
      if (createAccount && accountPassword) {
        try {
          const reg = await shopApi.post("/account/register", {
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            password: accountPassword,
          });
          localStorage.setItem("cc_customer_token", reg.data.token);
        } catch {
          // Account may already exist — order is still placed
        }
      }
      clearCart();
      if (r.data.payment_url) {
        window.location.assign(r.data.payment_url);
      } else {
        navigate(`/shop/order/${r.data.order_number}`);
      }
    } catch (e) {
      setError(formatApiError(e, "Could not place your order. Please try again."));
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <ShopLayout title="Checkout | Loctician Carlocs Shop">
        <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="checkout-empty">
          <p className="text-[#F5EFE3]/50 mb-6">Your cart is empty.</p>
          <Link to="/shop" className="text-[#C9A24B] underline underline-offset-4">Back to the shop</Link>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout title="Checkout | Loctician Carlocs Shop">
      <div className="max-w-6xl mx-auto px-5 py-10 md:py-16" data-testid="checkout-page">
        <h1 className="font-serif text-3xl md:text-5xl mb-10">
          Check<span className="italic text-[#C9A24B]">out</span>
        </h1>

        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 space-y-8">
            {/* Contact */}
            <section>
              <h2 className="font-serif text-xl mb-5 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-[#C9A24B]" /> Your Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label="Full Name *" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="checkout-name" />
                <Input label="Phone *" value={form.phone} onChange={set("phone")} placeholder="+27 ..." data-testid="checkout-phone" />
                <div className="sm:col-span-2">
                  <Input label="Email *" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="checkout-email" />
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={createAccount}
                      onChange={(e) => setCreateAccount(e.target.checked)}
                      data-testid="create-account-checkbox"
                      className="w-5 h-5 accent-[#C9A24B]"
                    />
                    <span className="text-sm text-[#F5EFE3]/70">Create an account to track this order</span>
                  </label>
                  {createAccount && (
                    <div className="mt-3">
                      <PasswordInput
                        label="Choose a Password *"
                        value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        data-testid="account-password"
                        toggleTestId="account-password-toggle"
                        className="w-full bg-[#232323] border border-[#C9A24B]/20 rounded-xl px-4 py-3.5 pr-12 text-[#F5EFE3] placeholder-[#F5EFE3]/30 focus:outline-none focus:border-[#C9A24B] transition-colors"
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Delivery */}
            <section>
              <h2 className="font-serif text-xl mb-5 flex items-center gap-2"><Truck className="w-5 h-5 text-[#C9A24B]" /> Delivery</h2>
              <div className="grid sm:grid-cols-3 gap-4 mb-5">
                {(config?.delivery_options || []).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setDelivery(opt.key)}
                    data-testid={`delivery-${opt.key}`}
                    className={`text-left p-5 rounded-2xl border transition-all duration-300 ${delivery === opt.key ? "border-[#C9A24B] bg-[#C9A24B]/10" : "border-[#C9A24B]/15 bg-[#232323]"}`}
                  >
                    {opt.key === "collection" ? <MapPin className="w-5 h-5 text-[#C9A24B] mb-2" /> : <Truck className="w-5 h-5 text-[#C9A24B] mb-2" />}
                    <p className="font-semibold text-sm leading-snug">{opt.label}</p>
                    <p className="text-[#C9A24B] text-sm mt-1.5">{opt.fee_cents === 0 ? "FREE" : fmt(opt.fee_cents)}</p>
                    {opt.note && <p className="text-[#F5EFE3]/40 text-xs mt-1">{opt.note}</p>}
                  </button>
                ))}
              </div>
              {delivery !== "collection" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Input label="Street Address *" value={form.street} onChange={set("street")} placeholder="Street and number" data-testid="checkout-street" />
                  </div>
                  <Input label="City *" value={form.city} onChange={set("city")} placeholder="City" data-testid="checkout-city" />
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-2">Province *</label>
                    <select
                      value={form.province}
                      onChange={set("province")}
                      data-testid="checkout-province"
                      className="w-full bg-[#232323] border border-[#C9A24B]/20 rounded-xl px-4 py-3.5 text-[#F5EFE3] focus:outline-none focus:border-[#C9A24B]"
                    >
                      {PROVINCES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <Input label="Postal Code *" value={form.postal} onChange={set("postal")} placeholder="2001" data-testid="checkout-postal" />
                </div>
              )}
            </section>

            {/* Payment */}
            <section>
              <h2 className="font-serif text-xl mb-5 flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#C9A24B]" /> Payment</h2>
              <div className="p-5 rounded-2xl border border-[#C9A24B] bg-[#C9A24B]/10" data-testid="payment-card">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-[#C9A24B] shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Secure Card Payment — iKhokha</p>
                    <p className="text-[#F5EFE3]/45 text-xs mt-1">You'll be taken to iKhokha's secure page to pay by card after placing your order.</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6 sticky top-24">
              <h2 className="font-serif text-xl mb-5">Order Summary</h2>
              {hasMadeToOrder && (
                <p data-testid="lead-time-notice" className="border border-[#C9A24B]/40 bg-[#C9A24B]/10 text-[#C9A24B] text-xs leading-relaxed rounded-xl px-4 py-3 mb-5">
                  Your order includes made-to-order locs extensions — handmade in 7-15 days. Delivery starts once they're ready.
                </p>
              )}
              <div className="space-y-3 mb-5">
                {items.map((i) => (
                  <div key={i.key} className="flex justify-between text-sm">
                    <span className="text-[#F5EFE3]/65 pr-3">{i.name}{i.size_label ? ` (${i.size_label})` : ""} × {i.qty}</span>
                    <span>{fmt(i.unit_price_cents * i.qty)}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mb-5">
                <input
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Discount code"
                  data-testid="discount-code-input"
                  className="flex-1 bg-[#1C1C1C] border border-[#C9A24B]/20 rounded-xl px-4 py-3 text-sm placeholder-[#F5EFE3]/30 focus:outline-none focus:border-[#C9A24B]"
                />
                <button onClick={applyDiscount} data-testid="apply-discount-btn" className="border border-[#C9A24B]/40 text-[#C9A24B] px-4 rounded-xl text-xs uppercase tracking-widest hover:bg-[#C9A24B] hover:text-[#1C1C1C] transition-colors">
                  Apply
                </button>
              </div>
              {discountError && <p className="text-red-400 text-xs mb-4" data-testid="discount-error">{discountError}</p>}
              {discount && <p className="text-[#C9A24B] text-xs mb-4" data-testid="discount-applied">Code {discount.code} applied — {fmt(discount.discount_cents)} off</p>}

              <div className="border-t border-[#C9A24B]/10 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-[#F5EFE3]/60"><span>Subtotal</span><span>{fmt(subtotalCents)}</span></div>
                {discount && <div className="flex justify-between text-[#C9A24B]"><span>Discount</span><span>-{fmt(discount.discount_cents)}</span></div>}
                <div className="flex justify-between text-[#F5EFE3]/60"><span>Delivery</span><span>{deliveryFee === 0 ? "Free" : fmt(deliveryFee)}</span></div>
                <div className="flex justify-between font-semibold text-lg pt-2"><span>Total</span><span className="text-[#C9A24B]" data-testid="checkout-total">{fmt(total)}</span></div>
              </div>

              {error && <p className="text-red-400 text-sm mt-4" data-testid="checkout-error">{error}</p>}

              <button
                onClick={placeOrder}
                disabled={busy}
                data-testid="place-order-btn"
                className="w-full mt-6 bg-[#C9A24B] text-[#1C1C1C] py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(201,162,75,0.3)] transition-all duration-300 disabled:opacity-50 disabled:translate-y-0"
              >
                {busy ? "Placing Order…" : "Pay Securely by Card"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ShopLayout>
  );
};

export default CheckoutPage;
